'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth, UserRole } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'login' | 'signup';
}

export default function AuthModal({ isOpen, onClose, initialTab = 'login' }: AuthModalProps) {
  const [tab, setTab] = useState<'login' | 'signup'>(initialTab);
  const [role, setRole] = useState<UserRole>('creator');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { loginAs } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      if (tab === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              role: role,
            },
          },
        });

        if (error) {
          throw error;
        }

        if (data.user) {
          // Upsert profile in public.profiles table
          await supabase.from('profiles').upsert({
            id: data.user.id,
            email: email,
            full_name: fullName || email.split('@')[0],
            role: role,
            avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
          });

          // Create default channel if registering as creator
          if (role === 'creator') {
            const handleName = (fullName || email.split('@')[0])
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '');
            await supabase.from('channels').upsert({
              user_id: data.user.id,
              channel_name: fullName ? `${fullName}'s Studio` : 'My Audio Channel',
              handle: handleName || `creator${Date.now().toString().slice(-4)}`,
              bio: 'Audio story creator on KissaFM.',
              logo_url: '/assets/images/Logo_main.jpeg',
              banner_url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4',
              is_verified: false,
              monetization_status: 'none',
            });
          }
        }

        setLoading(false);
        onClose();
      } else {
        // Log in
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          setErrorMessage(error.message);
          setLoading(false);
          return;
        }
        setLoading(false);
        onClose();
      }
    } catch (err: any) {
      console.warn('Auth submission info:', err);
      setErrorMessage(err.message || 'An error occurred during authentication.');
      setLoading(false);
    }
  };

  const handleQuickDemoLogin = (targetRole: UserRole) => {
    loginAs(targetRole);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-3xl border border-purple-500/30 p-6 md:p-8 w-full max-w-md shadow-2xl relative text-white space-y-6">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-full hover:bg-slate-800 transition-colors"
        >
          <Icon name="XMarkIcon" size={20} />
        </button>

        {/* Branding Logo */}
        <div className="text-center space-y-2">
          <div className="h-12 w-auto inline-flex items-center justify-center">
            <AppImage
              src="/assets/images/Logo_main.jpeg"
              alt="KissaFM Logo"
              width={140}
              height={44}
              className="h-11 w-auto object-contain rounded-md"
            />
          </div>
          <p className="text-xs text-purple-300 font-medium">Audio Stories, FM Shows & Creator Network</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800 text-sm font-bold">
          <button
            onClick={() => setTab('login')}
            className={`flex-1 py-2 rounded-lg transition-all ${
              tab === 'login' ? 'bg-purple-700 text-white shadow' : 'text-gray-400 hover:text-white'
            }`}
          >
            Log In
          </button>
          <button
            onClick={() => setTab('signup')}
            className={`flex-1 py-2 rounded-lg transition-all ${
              tab === 'signup' ? 'bg-purple-700 text-white shadow' : 'text-gray-400 hover:text-white'
            }`}
          >
            Sign Up
          </button>
        </div>

        {/* Error Notification */}
        {errorMessage && (
          <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-red-300 text-xs font-semibold">
            {errorMessage}
          </div>
        )}

        {/* Role Selector */}
        <div>
          <label className="block text-xs font-bold text-gray-300 uppercase mb-2">Account Type / Role</label>
          <div className="grid grid-cols-3 gap-2 text-xs font-bold">
            <button
              type="button"
              onClick={() => setRole('listener')}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                role === 'listener'
                  ? 'bg-purple-900/40 border-purple-500 text-purple-300 shadow'
                  : 'bg-slate-950 border-slate-800 text-gray-400 hover:border-slate-700'
              }`}
            >
              Listener
            </button>

            <button
              type="button"
              onClick={() => setRole('creator')}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                role === 'creator'
                  ? 'bg-pink-900/40 border-pink-500 text-pink-300 shadow'
                  : 'bg-slate-950 border-slate-800 text-gray-400 hover:border-slate-700'
              }`}
            >
              Creator Studio
            </button>

            <button
              type="button"
              onClick={() => setRole('super_admin')}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                role === 'super_admin'
                  ? 'bg-red-900/40 border-red-500 text-red-300 shadow'
                  : 'bg-slate-950 border-slate-800 text-gray-400 hover:border-slate-700'
              }`}
            >
              Super Admin
            </button>
          </div>
        </div>

        {/* Form Inputs */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === 'signup' && (
            <div>
              <label className="block text-xs font-bold text-gray-300 uppercase mb-1">Full Name / Channel Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Enter full name..."
                className="w-full p-3 bg-slate-950 text-white border border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-300 uppercase mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="name@kissafm.com"
              className="w-full p-3 bg-slate-950 text-white border border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-300 uppercase mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full p-3 bg-slate-950 text-white border border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white font-cta font-bold text-base rounded-xl shadow-lg hover:scale-105 transition-all mt-2"
          >
            {loading ? 'Authenticating...' : tab === 'login' ? `Log In as ${role.toUpperCase()}` : `Create ${role.toUpperCase()} Account`}
          </button>
        </form>

        {/* Demo Quick Logins */}
        <div className="pt-4 border-t border-slate-800 space-y-2">
          <p className="text-xs text-gray-400 text-center uppercase tracking-wider font-bold">Quick One-Click Demo Logins</p>
          <div className="grid grid-cols-2 gap-2 text-xs font-bold">
            <button
              type="button"
              onClick={() => handleQuickDemoLogin('creator')}
              className="py-2 px-3 bg-pink-950/40 text-pink-300 border border-pink-500/30 rounded-xl hover:bg-pink-900/60"
            >
              ★ Login Creator Studio
            </button>

            <button
              type="button"
              onClick={() => handleQuickDemoLogin('super_admin')}
              className="py-2 px-3 bg-red-950/40 text-red-300 border border-red-500/30 rounded-xl hover:bg-red-900/60"
            >
              🛡 Login Super Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
